import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_EXCLUSIONS_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { getDataProtectionExclusionTypeDropdownOptions } from '../../../../../../../utils/uiSettings/contentControl/content-control.values';
import {
  ContentControlDataProtectionExclusion,
  ContentControlDataProtectionExclusionType,
  ContentControlDataProtectionRule,
} from '@nirvana/shared/dtos/types/policies/common';
import _ from 'lodash';
import { GzContentControlDataProtectionRulesLightGridWebComponent } from '../dataProtectionRules/GzContentControlDataProtectionRulesLightGridWebComponent';
import {
  ContentControlDataProtectionRuleLight,
  ContentControlDataProtectionRuleLightStatus,
} from '../../../../../../../utils/uiSettings/contentControl/content-control.types';

@customElement(WEB_COMPONENT_POLICY_PANEL_CONTENT_CONTROL_DATA_PROTECTION_EXCLUSIONS_FORM)
export class GzContentControlWebDataProtectionExclusionsFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ContentControlDataProtectionExclusion> {
  @query('#address-field')
  private addressField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#type-field')
  private typeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rules-field')
  private rulesField: GzFormFieldWebComponent<
    GzContentControlDataProtectionRulesLightGridWebComponent,
    ContentControlDataProtectionRuleLight[]
  >;

  public constructor(private rules: ContentControlDataProtectionRule[], value?: ContentControlDataProtectionExclusion) {
    super(value);
  }

  static get styles() {
    return styles;
  }

  public setItemNameError(error: string) {
    this.addressField.setError(error);
  }

  public setRulesToConsiderError(error: string) {
    this.rulesField.setError(error);
  }

  protected getConvertedFormValue() {
    const formValue = _.cloneDeep(this.formGroup.value);
    const rules = _.get(formValue, 'rulesToConsider', []) as ContentControlDataProtectionRuleLight[];
    const rulesToConsider = rules
      .filter(rule => rule.status === ContentControlDataProtectionRuleLightStatus.ENABLED)
      .map(rule => {
        return {
          ruleName: rule.name,
        };
      });
    _.set(formValue, 'rulesToConsider', rulesToConsider);

    return formValue;
  }

  createFormGroup(): void {
    this.addressField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.typeField.Validators = [RequiredValidator(translateFn('Required field'))];

    this.formGroup = FormGroup.build({
      address: this.addressField,
      type: this.typeField,
      rulesToConsider: this.rulesField,
    });
  }

  setFormGroupValue() {
    const selectedRules: ContentControlDataProtectionRuleLight[] = this.rules.map(rule => {
      return {
        name: rule.name,
        status: this.value?.rulesToConsider.some(selectedRule => selectedRule.ruleName === rule.name)
          ? ContentControlDataProtectionRuleLightStatus.ENABLED
          : ContentControlDataProtectionRuleLightStatus.DISABLED,
      };
    });

    if (!this.value) {
      this.rulesField.value = selectedRules;
      return;
    }

    this.formGroup.value = {
      address: this.value.address,
      type: this.value.type,
      rulesToConsider: selectedRules,
    };
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <gz-title fontWeight="600" fontSize="11" class="module-subtitle">${translateFn('Details')}</gz-title>
        <gz-form-field
          id="address-field"
          class="label-mw-250 mt-2"
          label="${translateFn('Website or email address')}"
          required
        >
          <gz-input
            inputWidth="${InputWidthValues.LARGE}"
            gz-form-input
            placeholder="${translateFn('Enter the exclusion name')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="type-field" label="${translateFn('Exclusion type')}" required>
          <gz-dropdown
            .value="${ContentControlDataProtectionExclusionType.WEB}"
            gz-form-input
            options="${JSON.stringify(getDataProtectionExclusionTypeDropdownOptions())}"
          ></gz-dropdown>
        </gz-form-field>

        <gz-title fontWeight="600" fontSize="11" class="module-subtitle mt-6">${translateFn('Rules')}</gz-title>
        <gz-text fontSize="12" fontWeight="400" class="mt-2"
          >${translateFn('Select the rules to which this exclusion will be applied.')}
        </gz-text>
        <gz-form-field id="rules-field">
          <gz-policy-panel-content-control-data-protection-rules-grid-light
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-content-control-data-protection-rules-grid-light>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }
}
